import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Tag } from 'lucide-react';
import { Input } from '@/components/ui';
import { formatCurrency } from '@/utils/formatters';
import type { useCartController } from './useCartController';

type Cart = ReturnType<typeof useCartController>;

/**
 * Cart-level discount with a Rp | % switch. Percent is only an input convenience: it is converted
 * against the server subtotal and the same capped `setDiscount` runs, so no money math moves client-side.
 */
export const DiscountField: React.FC<{ cart: Cart }> = ({ cart }) => {
    const { t } = useTranslation();
    const [mode, setMode] = useState<'amount' | 'percent'>('amount');
    const [pct, setPct] = useState('');
    const subtotal = cart.calculation?.subtotal ?? 0;
    const cap = Number.isFinite(cart.discountCap) ? cart.discountCap : cart.calculation?.maxDiscount ?? 0;
    const left = Math.max(0, cap - cart.totalDiscount);

    const applyPercent = (raw: string) => {
        const clean = raw.replace(/[^\d.]/g, '');
        setPct(clean);
        const p = Math.min(100, parseFloat(clean) || 0);
        cart.setDiscount(Math.round((subtotal * p) / 100));
    };

    const toggle = (
        <span className="flex gap-0.5 rounded-pill bg-surface-sunken p-0.5 text-[11px] font-black">
            {(['amount', 'percent'] as const).map((m) => (
                <button key={m} type="button" onClick={() => { setMode(m); if (m === 'percent') setPct(''); }} className={clsx('rounded-pill px-2 py-0.5', mode === m ? 'bg-surface-card text-ink-brand shadow-xs' : 'text-ink-muted')}>
                    {m === 'amount' ? 'Rp' : '%'}
                </button>
            ))}
        </span>
    );

    return (
        <Input
            fieldSize="sm"
            type={mode === 'amount' ? 'number' : 'text'}
            inputMode={mode === 'amount' ? 'numeric' : 'decimal'}
            min={0}
            step={mode === 'amount' ? 1000 : undefined}
            placeholder="0"
            value={mode === 'amount' ? cart.totalDiscount || '' : pct}
            onChange={(e) => (mode === 'amount' ? cart.setDiscount(parseFloat(e.target.value) || 0) : applyPercent(e.target.value))}
            leftIcon={<span className="flex items-center gap-1.5 text-xs font-extrabold text-ink-secondary"><Tag className="h-[14px] w-[14px]" />{t('cart.discountLabel')}</span>}
            rightIcon={toggle}
            helperText={cap > 0 && cap !== Infinity ? t('cart.maxDiscountPerTx', { max: formatCurrency(cap), left: formatCurrency(left) }) : undefined}
        />
    );
};
export default DiscountField;
